import { memoize } from "@use/decorators";

import { ElementModel, ElementModelData } from "@courses-pm2/api/src/ts/models/ElementModel";
import { InternalLinkTargets } from "@courses-pm2/api/src/ts/models/elements/InternalLinkModel";
import { reduceRichTextNodeStandardPageCount, renderContentNodes, RichtextContentNode } from "@courses-pm2/api/src/ts/models/elements/RichtextModel";

/**
 * Specification of data needed to instantiate a new ElementLinkModel.
 */
type ElementLinkData = {
    [K in "portalId" | "elementId" | "target" | "content"]: ElementLinkModel[K];
};

/**
 * Describes the content to be rendered for the element, so that it's made
 * easily available.
 */
export class ElementLinkModel extends ElementModel {
    /**
     * Specifies the id of the portal that the link belongs to (this is
     * necessary to generate the absolute url of the page, if the link is
     * rendered in another context than the portal itself).
     */
    public readonly portalId: string;

    /**
     * Specifies the id of the element that this link should be pointing to.
     */
    public readonly elementId: string;

    /**
     * Specifies the desired target, which the content of the link should be
     * opened at.
     */
    public readonly target: InternalLinkTargets;

    /**
     * Contains the content that should be rendered within the glossary word
     * trigger node.
     */
    public readonly content: RichtextContentNode[];

    /**
     * Apply data given when a new model is being instantiated.
     */
    constructor(initial: ElementLinkData & ElementModelData) {
        super(initial);

        this.portalId = initial.portalId;
        this.elementId = initial.elementId;
        this.target = initial.target;
        this.content = initial.content;
    }

    /**
     * Helper that'll transform the content of the GlossaryWord into React nodes
     * that can be mounted directly.
     */
    @memoize()
    public render(): React.ReactNode[] {
        if (!this.engine) {
            throw new Error("ElementLinkModel.render(): No rendering engine provided to this model.");
        }

        return renderContentNodes(this.content, this.engine);
    }

    /**
     * Preview is unavailable for InternalLinks.
     */
    public async getPreviewImageUrl(): Promise<undefined> {
        return undefined;
    }

    /**
     * Preview is unavailable for InternalLinks.
     */
    public getPreviewText(): string | undefined {
        return undefined;
    }

    /**
     * @inheritdoc
     *
     * Element links are able to contain RichTextNodes, so use the helper from
     * the RichtextModel to calculate standard pages for the content.
     */
    // tslint:disable-next-line: completed-docs
    @memoize()
    public getStandardPageCount(): number {
        return reduceRichTextNodeStandardPageCount(this.content);
    }
}
